import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import cx from 'clsx';

import { IconScam } from '@/assets/history';
import { IconFailedCircle } from '@/assets/swap';
import { ChangeTokenList } from '@/components';
import {
  numSeparate,
  shortenAddress,
  getExplorerLink,
  HistoryGetTimeAgo,
  CHAINS_DICT,
  formatNetworth,
} from '@/utils';

import { TransactionAction } from '../components';
import { TokenHistoryItem, ContentScope, CexDetail, TransactionProject } from '../types';
import { updateHistoryNftInfo } from '../hooks';
import style from '../index.module.css';

interface HistoryRowProps {
  cancel?: Function;
  currentNonce?: number;
  info: TokenHistoryItem;
  projects: Record<string, TransactionProject>;
  cexs: Record<string, CexDetail>;
  memos: Record<string, string>;
  cate: {
    [index: string]: {
      name: string;
      id: string;
    };
  };
  tokens: Record<string, Tokens>;
  chain: string;
  onTokenClick?(arg: any): void;
  scope: ContentScope;
}

type TxStatusProps = Pick<HistoryRowProps, 'info' | 'chain'>;

const TxStatus = ({ info, chain }: TxStatusProps) => {
  const { t } = useTranslation();

  const isFailed = info.tx?.status === 0;
  const isScam = info?.is_scam;
  const sinceTime =
    Date.now() / 1000 - info.time_at < 3600 * 24
      ? `${HistoryGetTimeAgo(info.time_at)} ago`
      : dayjs(info.time_at * 1000).format('YYYY/MM/DD HH:mm:ss');

  const shortenTxId = (
    <a target={'_blank'} rel="noreferrer" href={`${getExplorerLink(info.chain, info.id)}`}>
      {shortenAddress(info.id)}
    </a>
  );

  const status = useMemo(() => {
    if (isScam) {
      return (
        <div className={style.scam}>
          <IconScam />
        </div>
      );
    } else if (isFailed) {
      return (
        <div className={style.failed}>
          <img src={IconFailedCircle} alt="" />
          {t('components_Row.yoexct')}
        </div>
      );
    }
    return null;
  }, [isFailed, isScam, t]);

  return (
    <div className={style.txStatus}>
      {status}
      <div className={style.sinceTime}>{sinceTime}</div>
      <div>
        {!chain && (
          <img className={style.rowChain} src={CHAINS_DICT[info.chain]?.logo} alt={info.chain} />
        )}
        {shortenTxId}
      </div>
    </div>
  );
};

const TokensChange = ({
  info,
  tokens,
  onTokenClick,
  scope,
  chain,
}: Pick<HistoryRowProps, 'info' | 'tokens' | 'chain' | 'scope'> & {
  onTokenClick?(arg: any): void;
}) => {
  const handleTokenClick = useCallback(
    ({
      id,
      name,
      chain: tokenChain,
      uuid,
    }: {
      id: string;
      name: string;
      chain: string;
      uuid: string;
    }) => {
      const isNFT = id.length === 32;

      if (!isNFT) {
        onTokenClick?.({
          id,
          name,
          tokenChain,
          uuid,
        });
      }
    },
    [onTokenClick],
  );

  const handleNftRefresh = useCallback(
    res => {
      updateHistoryNftInfo(scope, res?.nft);
    },
    [scope],
  );

  return (
    <ChangeTokenList
      sends={info.sends}
      receives={info.receives}
      tokenDict={tokens}
      onTokenClick={handleTokenClick}
      onNftRefresh={handleNftRefresh}
      time={info.time_at}
      chain={chain}
      withFlag
    />
  );
};

const HistoryRow = (props: HistoryRowProps) => {
  const { t } = useTranslation();

  const { info, projects, cexs, cate, tokens, chain, onTokenClick, scope, memos } = props;
  const isFailed = info.tx?.status === 0;
  const isScam = info?.is_scam;

  return (
    <div className={cx(style.tableLine, (isFailed || isScam) && style.error)}>
      <TxStatus info={info} chain={chain} />

      <TransactionAction
        info={info}
        projectsDict={projects}
        cexs={cexs}
        cate={cate}
        tokensDict={tokens}
        memos={memos}
      />

      <TokensChange
        info={info}
        tokens={tokens}
        chain={info.chain}
        onTokenClick={onTokenClick}
        scope={scope}
      />

      <div className={style.txExplain}>
        {info.msg && (
          <div>
            <span className={style.txExplainHead}>{t('components_Row.17bmnl3')}</span>
            <div>{info.msg}</div>
          </div>
        )}
        {info.tx && info.tx?.eth_gas_fee ? (
          <div>
            <span className={style.txExplainHead}>{t('components_Row.ocoynz')}</span>
            <span className={style.txGasInfo}>
              {' '}
              {numSeparate(info.tx?.eth_gas_fee, 4)}
              {info.chain && CHAINS_DICT[info.chain]?.nativeTokenSymbol}
              <span className={style.gasPrice}>({formatNetworth(info.tx?.usd_gas_fee)})</span>
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default HistoryRow;
